import React, { useCallback, useRef } from "react";
import {
  Flex,
  Switch,
  Button,
  Popover,
  PopoverTrigger,
  PopoverContent,
  Input,
  Text,
} from "@appsmith/ads";
import type { Tool } from "../types";

interface ToolEditControlsProps {
  icon: React.ReactNode;
  tool: Tool;
  isEditing: boolean;
  setIsEditing: (isEditing: boolean) => void;
  handleDescriptionSave: (value: string) => void;
  handleApprovalChange: (isApprovalRequired: boolean) => void;
}

export const ToolEditControls = (props: ToolEditControlsProps) => {
  const {
    handleApprovalChange,
    handleDescriptionSave,
    icon,
    isEditing,
    setIsEditing,
    tool,
  } = props;
  const descriptionInputRef = useRef<HTMLInputElement>(null);

  const blockEventPropagation = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      event.stopPropagation();
    },
    [],
  );

  const onSaveClick = useCallback(() => {
    handleDescriptionSave(descriptionInputRef.current?.value || "");
  }, [handleDescriptionSave]);

  const onCancelClick = useCallback(() => {
    setIsEditing(false);
  }, [setIsEditing]);

  return (
    <Flex
      alignItems="center"
      className="tool-edit-controls"
      gap="spaces-3"
      onClick={blockEventPropagation}
    >
      <Switch
        isDisabled={!tool.isSelected}
        isSelected={tool.isApprovalRequired}
        onChange={handleApprovalChange}
      >
        Requires Approval
      </Switch>
      {!tool.systemGenerated ? (
        <Popover onOpenChange={setIsEditing} open={isEditing}>
          <PopoverTrigger>
            <Button
              isDisabled={!tool.isSelected}
              isIconButton
              kind="secondary"
              startIcon="pencil-line"
            />
          </PopoverTrigger>
          <PopoverContent size="md">
            <Flex flexDirection="column" padding="spaces-3">
              <Flex flexDirection="column" gap="spaces-5">
                <Flex alignItems="center" gap="spaces-2">
                  {icon}
                  <Text kind="heading-s">{tool.name}</Text>
                </Flex>
                <Input
                  UNSAFE_height="108px"
                  defaultValue={tool.description}
                  placeholder="Edit your description here"
                  ref={descriptionInputRef}
                  renderAs="textarea"
                  size="md"
                />
                <Flex gap="spaces-2" justifyContent="flex-end">
                  <Button kind="secondary" onClick={onCancelClick} size="md">
                    Cancel
                  </Button>
                  <Button kind="primary" onClick={onSaveClick} size="md">
                    Save
                  </Button>
                </Flex>
              </Flex>
            </Flex>
          </PopoverContent>
        </Popover>
      ) : null}
    </Flex>
  );
};
